/*
 * Copyright 1999-2024 Jagex Ltd.
 */
import { AxiosRequestConfig } from 'axios';
import { action, observable, computed } from 'mobx';
import { Auth } from '@stores/auth/auth';
import { auth } from '@stores/auth/decorators';
import { loader } from '@stores/loading/decorators';
import { Loading } from '@stores/loading/loading';
import { Notifications } from '@stores/notifications/notifications';
import { MOCK_USER_ACCOUNT_INFO } from '@views/linkedAccount/LinkedAccountsPage/test-data';
import { Request } from './requests';
import { UserAccountTypeInfo, UserType, UserAccountInfo } from './types';

export class Users {
  @observable private _userAccountInfo: UserAccountInfo | null = null;
  @observable private _userAccountTypeInfo: UserAccountTypeInfo | null = null;
  @observable private _isCharacterSelected = false;

  constructor(private auth: Auth, private loading: Loading, private notifications: Notifications) {
    this.loading.registerLoader('users--get-user-account-info', true);
  }

  @computed
  public get userAccountInfo(): UserAccountInfo | null {
    return this._userAccountInfo;
  }

  public get userAccountTypeInfo(): UserAccountTypeInfo | null {
    return this._userAccountTypeInfo;
  }

  public get isRestricted(): boolean {
    return this._userAccountTypeInfo
      ? this._userAccountTypeInfo?.type === UserType.Jagex && !this._userAccountTypeInfo?.hasRSAccount
      : true;
  }

  public get isCharacterSelected(): boolean {
    return this._isCharacterSelected;
  }

  @action
  setUserAccountInfo(userAccountInfo: UserAccountInfo): void {
    this._userAccountInfo = userAccountInfo;
  }

  @action
  setIsCharacterSelected(isCharacterSelected: boolean): void {
    this._isCharacterSelected = isCharacterSelected;
  }

  @loader('users--get-user-account-selection')
  @auth
  async getUserAccountSelection(config: AxiosRequestConfig): Promise<void> {
    const { RSAccountsSelectUrl } = await Request.getUserAccountSelection(config);

    if (RSAccountsSelectUrl) {
      window.location.assign(RSAccountsSelectUrl);
    } else {
      this.setIsCharacterSelected(true);
    }
  }

  @loader('users--get-user-account-info')
  @auth
  async getUserAccountInfo(): Promise<void> {
    const userAccountInfo = await Request.getUserAccountInfo();
    this.setUserAccountInfo(userAccountInfo);
  }
}
